import React, { Component } from 'react';
import axios from 'axios';
import {
    Typography,
    withStyles,
    CircularProgress,
    Button
} from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import { Auth } from 'aws-amplify';
import { Redirect, Link } from 'react-router-dom';
import moment from 'moment';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import PassengerForm from '../components/PassengerForm';
import ConfirmPurchase from '../components/ConfirmPurchase';
import ConfirmBankPurchase from '../components/ConfirmBankPurchase';
import ConfirmDirectDebitPurchase from '../components/ConfirmDirectDebitPurchase';
import { getOrientation, resetOrientation } from '../helpers/image';
import { getYearGroups, hasValidZonePricing } from '../helpers/ticket';
import { contactOperatorString } from '../helpers/contact';
import { getAcademicYear } from '../helpers/date';

const styles = theme => ({
    root: {
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    error: {
        margin: '20px 0 0 5px',
        color: red[500],
        minHeight: '20px',
        textAlign: 'center'
    },
    spinner: {
        marginLeft: 'calc(50% - 22px)',
        marginTop: '60px'
    },
    availabilityWarning: {
        background: 'red',
        textAlign: 'center',
        marginBottom: 20,
        padding: 5
    },
    availabilityWarningHeading: {
        color: 'white',
        fontWeight: 'bold',
        fontSize: '1rem'
    },
    availabilityWarningText: {
        color: 'white'
    },
    modalSplash: {
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
        height: '100vh',
        width: '100vw',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'scroll',
        background: 'rgba(0, 0, 0, 0.6)'
    },
    modalContainer: {
        maxWidth: '600px',
        padding: '25px 25px 35px 25px',
        background: 'white',
        borderRadius: '4px',
        [theme.breakpoints.down('xs')]: {
            padding: '15px 15px 25px 15px'
        }
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&> h5': {
            [theme.breakpoints.down('xs')]: {
                fontSize: '1.3rem'
            }
        }
    },
    existingTickets: {
        display: 'flex',
        flexDirection: 'column',
        margin: '20px 0',
        alignItems: 'center',
        borderTop: '1px solid #eee'
    },
    existingTicket: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        borderBottom: '1px solid #eee',
        padding: '5px 0'
    },
    existingTicketDetails: {
        display: 'flex',
        flexDirection: 'column'
    },
    breakLine: {
        width: '100%',
        height: '1px',
        background: 'black',
        margin: '0 0 20px'
    },
    modalButtons: {
        alignItems: 'center',
        marginTop: '30px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
    },
    existingTicketButton: {
        alignSelf: 'center',
        fontSize: '0.9rem',
        padding: '2px 5px'
    },
    duplicateWarn: {
        fontSize: '0.9rem',
        textAlign: 'center'
    },
    errorLink: {
        color: 'red'
    }
});

class CreateTicket extends Component {
    constructor(props) {
        super(props);

        this.state = {
            error: '',
            imageError: '',
            passengerData: {
                firstName: '',
                lastName: '',
                stop: '',
                dest: '',
                photo: null,
                startDate: null,
                startTime: '',
                yr: '',
                zone: '',
                zoneStop: '',
                id: ''
            },
            passengerSubmitted: false,
            imagePreviewUrl: null,
            ticket: null,
            existingTickets: null,
            showCheckTickets: false,
            client: null,
            service: null,
            serviceAvailability: null,
            mandates: null,
            isLoading: true,
            paymentIntent: null
        };
    }

    async componentDidMount() {
        const {
            match: {
                params: { clientSlug, year, serviceId, ticketId }
            },
            classes
        } = this.props;

        let client;

        try {
            client = await this.getClientData(clientSlug, serviceId);
        } catch (e) {
            this.setState({
                isLoading: false,
                error:
                    'There was a problem retrieving the ticket. Please try again later or contact our support team.'
            });
            return;
        }

        try {
            if (
                process.env.REACT_APP_DISABLE_EXPIRED_TICKETS === '1' &&
                getAcademicYear() > year.replace('-', '/')
            ) {
                throw new Error('Expired ticket year');
            }

            const service = this.extractService(client, serviceId);
            const ticket = await this.getTicketData(year, service, ticketId);

            const tickets = await this.getCustomerTickets();
            const now = new Date();
            const existingTickets = tickets.data.items.filter(
                customerTicket =>
                    customerTicket.ticket_id === ticket.id &&
                    ['annual', 'autumn', 'spring', 'summer', 'flexi'].includes(
                        ticket.type
                    ) &&
                    now < new Date(customerTicket.expiry) &&
                    customerTicket.ticket_status !== 'void'
            );

            const activeFlexiTicketsInDebit = tickets.data.items.filter(
                ct =>
                    ct.type === 'flexi' &&
                    now < new Date(ct.expiry) &&
                    ct.ticket_status !== 'void' &&
                    ct.trips - ct.trips_taken < 0
            );

            if (activeFlexiTicketsInDebit && activeFlexiTicketsInDebit.length) {
                throw new Error('Flexi debit on account');
            }

            let serviceAvailability = null;

            if (
                ticket.type === 'single' ||
                ticket.type === 'return' ||
                ticket.type === 'week' ||
                ticket.type === '4_week'
            ) {
                serviceAvailability = await this.getServiceAvailability(
                    clientSlug,
                    serviceId,
                    year
                );
            }

            let mandates = [];
            if (ticket && ticket.payment_type === 'dd') {
                mandates = await this.getMandates(client.id);
            }

            this.setState({
                client,
                ticket,
                existingTickets,
                showCheckTickets: existingTickets.length > 0,
                service,
                mandates,
                serviceAvailability,
                isLoading: false,
                error: ''
            });
        } catch (e) {
            console.error(e);
            let errorMessage =
                'There was a problem retrieving the ticket. Please try again later or contact our support team.';

            if (e.message === 'Ticket unavailable') {
                errorMessage = `This ticket type is now sold out. Please contact ${contactOperatorString(
                    client
                )} for more information regarding ticket availability.`;
            }

            if (e.message === 'Flexi debit on account') {
                errorMessage = (
                    <>
                        <p>
                            Your account has an active flexi ticket in debit.
                            Ticket purchasing on your account is disabled until{' '}
                            you have topped up your existing flexi ticket to{' '}
                            settle the balance due.
                        </p>
                        <p>
                            Flexi tickets can be topped up from the{' '}
                            <Link className={classes.errorLink} to="/">
                                My Tickets
                            </Link>{' '}
                            view or from the email notifications previously sent{' '}
                            to you.
                        </p>
                        <p>
                            Please contact {contactOperatorString(client)} for{' '}
                            more information.
                        </p>
                    </>
                );
            }

            if (e.message === 'Ticket disabled') {
                errorMessage = `Ticket disabled by your transport provider. Please contact ${contactOperatorString(
                    client
                )} for more information.`;
            }

            if (e.message === 'Ticket expired') {
                errorMessage = `This ticket type has a fixed expiry date which has now expired. Please contact ${contactOperatorString(
                    client
                )} for an alternative ticket type to purchase.`;
            }

            if (e.message === 'Zonal pricing misconfiguration') {
                errorMessage = `Zonal pricing on this ticket has not been configured. Please contact ${contactOperatorString(
                    client
                )} to request they update their pricing.`;
            }

            if (e.message === 'Expired ticket year') {
                errorMessage = `This ticket type was valid for a previous year and has now expired. Please contact ${contactOperatorString(
                    client
                )} for an alternative ticket type to purchase.`;
            }

            if (e.message === 'No service') {
                errorMessage = `This service has either been discontinued or does not exist. Please contact ${contactOperatorString(
                    client
                )} for an alternative ticket type to purchase.`;
            }

            if (
                e.response &&
                e.response.status === 503 &&
                e.response.data.error &&
                e.response.data.error.key === 'gocardless.offline'
            ) {
                errorMessage = e.response.data.error.message;
            }

            this.setState({
                isLoading: false,
                error: errorMessage
            });
        }
    }

    getCustomerTickets = async () => {
        const currentSession = await Auth.currentSession();
        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        return axios.get(
            `${
                process.env.REACT_APP_API_ENDPOINT
            }/customer-ticket/list-by/user-id/${
                currentSession.getAccessToken().payload.sub
            }`,
            {
                headers
            }
        );
    };

    getClientData = async clientSlug => {
        const response = await axios.get(
            `${process.env.REACT_APP_API_ENDPOINT}/client/slug/${clientSlug}`
        );

        return response.data;
    };

    getTicketData = async (year, service, ticketId) => {
        const currentSession = await Auth.currentSession();
        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const response = await axios.get(
            `${process.env.REACT_APP_TICKET_API_ENDPOINT}/ticket/${ticketId}/detailed`,
            { headers }
        );

        if (!response.data) {
            throw new Error();
        }

        const ticket = response.data;

        if (!ticket) {
            throw new Error();
        }

        if (ticket.is_enabled === false) {
            throw new Error('Ticket disabled');
        }

        if (
            ticket.year !== year.replace('-', '/') ||
            ticket.service_id !== service.id
        ) {
            throw new Error();
        }

        if (ticket.available === false) {
            throw new Error('Ticket unavailable');
        }

        if (ticket.pricing_model === 'Zonal') {
            if (!hasValidZonePricing(ticket, service)) {
                throw new Error('Zonal pricing misconfiguration');
            }
        }

        if (
            ticket.expiry &&
            process.env.REACT_APP_DISABLE_EXPIRED_TICKETS === '1'
        ) {
            const now = moment();
            const expiry = moment(ticket.expiry);

            if (now.toISOString() > expiry.toISOString()) {
                throw new Error('Ticket expired');
            }
        }

        return ticket;
    };

    getServiceAvailability = async (clientSlug, serviceId, year) => {
        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const response = await axios.get(
            `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/service-availability/${clientSlug}/${serviceId}/${year}`,
            {
                headers
            }
        );

        return response.data;
    };

    getMandates = async clientId => {
        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const response = await axios.get(
            `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/customer-mandate/client/${clientId}`,
            {
                headers
            }
        );

        return response.data;
    };

    handleChange = event => {
        const { passengerData } = this.state;
        this.setState({
            passengerData: {
                ...passengerData,
                [event.target.name]: event.target.value
            },
            error: ''
        });
    };

    handleDateChange = date => {
        const { passengerData } = this.state;
        this.setState({
            passengerData: {
                ...passengerData,
                startDate: date
            },
            error: ''
        });
    };

    /**
     * @NOTE: Some pretty mad gymnastics going on to preview the image correctly as orientation may be stored in EXIF data
     * @SEE: https://stackoverflow.com/questions/7584794/accessing-jpeg-exif-rotation-data-in-javascript-on-the-client-side/32490603#32490603
     * @SEE: https://stackoverflow.com/questions/20600800/js-client-side-exif-orientation-rotate-and-mirror-jpeg-images
     */
    handleImageChange = event => {
        const reader = new FileReader();
        const file = event.target.files[0];
        const { passengerData } = this.state;

        this.setState({ imageError: '', imagePreviewUrl: '' });

        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                this.setState({
                    imageError: 'Photo must be no more than 10MB'
                });
            } else {
                reader.onloadend = () => {
                    getOrientation(file, srcOrientation => {
                        resetOrientation(
                            reader.result,
                            srcOrientation,
                            destBase64 => {
                                this.setState({
                                    passengerData: {
                                        ...passengerData,
                                        photo: file
                                    },
                                    imagePreviewUrl: destBase64
                                });
                            }
                        );
                    });
                };
                reader.readAsDataURL(file);
            }
        }

        event.preventDefault();
    };

    handleSubmitPassenger = event => {
        event.preventDefault();
        this.setState({
            passengerSubmitted: true
        });
        // @NOTE: Hack to jump user to top of page on submit, as the route doesn't change
        // resulting in unexpected landing half way down subsequent view.
        window.scrollTo(0, 0);
    };

    handleEditPassenger = ({ paymentIntent = null } = {}) => {
        this.setState({
            passengerSubmitted: false,
            paymentIntent
        });
    };

    passengerValid = () => {
        const { passengerData, service, client } = this.state;

        let stopSelectionValid = true;
        let yearGroupValid = true;
        let destinationSelectionValid = true;
        let zoneSelectionValid = true;

        const useZones = service.zones && passengerData.zone;

        if (service && 'stops' in service && !useZones) {
            stopSelectionValid = service.stops.includes(passengerData.stop);
        }

        if (useZones) {
            const zone =
                passengerData.zone &&
                service.zones.find(
                    serviceZone => serviceZone.name === passengerData.zone
                );

            if (zone && (!zone.stops || !zone.stops.length)) {
                zoneSelectionValid = true;
            } else if (
                zone &&
                zone.stops &&
                zone.stops.includes(passengerData.zoneStop)
            ) {
                zoneSelectionValid = true;
            } else {
                zoneSelectionValid = false;
            }
        }

        if (service && service.destinations && service.destinations.length) {
            destinationSelectionValid = service.destinations.includes(
                passengerData.dest
            );
        }

        if (service && getYearGroups(service.yr_groups).length) {
            if (!passengerData.yr) {
                yearGroupValid = false;
            }
        }

        const isPhotoValid =
            client.features.disable_photo === true || passengerData.photo;

        const requireStudentId = client.features.require_id === 'student';
        const isIdValid =
            !requireStudentId || (requireStudentId && passengerData.id);

        return (
            stopSelectionValid &&
            destinationSelectionValid &&
            passengerData.firstName &&
            passengerData.lastName &&
            isPhotoValid &&
            yearGroupValid &&
            zoneSelectionValid &&
            isIdValid
        );
    };

    showConfirmPurchase = () => {
        const { ticket, passengerSubmitted, service } = this.state;

        return this.passengerValid() && ticket && service && passengerSubmitted;
    };

    formatRemainingTickets = ticket => {
        const ticketWord = ticket.remaining > 1 ? 'tickets' : 'ticket';
        return `Only ${ticket.remaining} ${ticketWord} left, hurry!`;
    };

    extractService(client, serviceId) {
        try {
            const { services = [] } = client;

            if (!services.length) {
                throw new Error();
            }

            const service = services.find(item => item.id === serviceId);

            if (!service) {
                throw new Error('No service');
            }

            return service;
        } catch (e) {
            this.setState({
                error:
                    'There was a problem retrieving the ticket info, please try again later'
            });
            return null;
        }
    }

    renderTickets(existingTickets) {
        const { classes } = this.props;

        return (
            <div className={classes.existingTickets}>
                {existingTickets.map(ticket => {
                    const {
                        id,
                        type,
                        passenger,
                        date_purchased: datePurchased,
                        expiry,
                        trips,
                        trips_taken: tripsTaken
                    } = ticket;

                    return (
                        <div key={id} className={classes.existingTicket}>
                            <div className={classes.existingTicketDetails}>
                                <Typography variant="body2">
                                    <strong>
                                        {passenger.first_name}{' '}
                                        {passenger.last_name}
                                    </strong>
                                    {type === 'flexi' && (
                                        <span style={{ color: 'gray' }}>
                                            {' '}
                                            {trips - tripsTaken} trips
                                        </span>
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    Purchased{' '}
                                    {moment(datePurchased).format(
                                        'ddd Do MMM HH:mm'
                                    )}
                                </Typography>
                                {type === 'flexi' && (
                                    <Typography variant="body2">
                                        Expires{' '}
                                        {moment(expiry).format(
                                            'ddd Do MMM HH:mm'
                                        )}
                                    </Typography>
                                )}
                            </div>
                            {type === 'flexi' ? (
                                <Button
                                    className={classes.existingTicketButton}
                                    variant="outlined"
                                    color="primary"
                                    href={`/top-up/${id}`}
                                >
                                    Top up
                                </Button>
                            ) : (
                                <Button
                                    className={classes.existingTicketButton}
                                    variant="outlined"
                                    color="primary"
                                    href={`/ticket/${id}`}
                                >
                                    View
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderCheckTicketsModal(ticket, existingTickets) {
        const { classes } = this.props;

        let heroBody = 'You have purchased this ticket type before:';
        let actionBody = 'Are you sure you want to buy another?';
        if (ticket.type === 'flexi') {
            heroBody =
                'You have purchased this ticket type before. Flexi tickets are active until the expiry date listed on your ticket. Until it expires, it just needs topping up with more credit.';
            actionBody =
                existingTickets.length > 1
                    ? 'Would you like to top up an existing ticket instead?'
                    : 'Would you like to top up this ticket instead?';
        }

        return (
            <div className={classes.modalSplash}>
                <div className={classes.modalContainer}>
                    <div className={classes.modalHeader}>
                        <Typography variant="h5">
                            Duplicate purchase check
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={() => {
                                this.setState({ showCheckTickets: false });
                            }}
                        >
                            <Icon>close</Icon>
                        </IconButton>
                    </div>
                    <div className={classes.breakLine} />
                    <Typography
                        className={classes.duplicateWarn}
                        variant="body1"
                    >
                        {heroBody}
                    </Typography>
                    {this.renderTickets(existingTickets)}
                    <Typography variant="body1" align="center">
                        {actionBody}
                    </Typography>
                    {ticket.type === 'flexi' ? (
                        <div className={classes.modalButtons}>
                            {existingTickets.length > 1 ? (
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    href="/"
                                >
                                    Yes, view my tickets
                                </Button>
                            ) : (
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    href={`/top-up/${existingTickets[0].id}`}
                                >
                                    Yes, top up my ticket
                                </Button>
                            )}
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={() => {
                                    this.setState({ showCheckTickets: false });
                                }}
                            >
                                No, buy another flexi ticket
                            </Button>
                        </div>
                    ) : (
                        <div className={classes.modalButtons}>
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={() => {
                                    this.setState({ showCheckTickets: false });
                                }}
                            >
                                Yes, buy another
                            </Button>
                            <Button color="primary" variant="outlined" href="/">
                                No, view my tickets
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const {
            passengerData,
            imagePreviewUrl,
            imageError,
            ticket,
            existingTickets,
            client,
            service,
            serviceAvailability,
            showCheckTickets,
            error,
            mandates,
            isLoading,
            paymentIntent
        } = this.state;

        const {
            classes,
            match: {
                params: { year, serviceId, ticketId }
            }
        } = this.props;

        if (isLoading) {
            return (
                <div className={classes.root}>
                    <CircularProgress
                        id="loading-spinner"
                        className={classes.spinner}
                    />
                </div>
            );
        }

        if (error) {
            return (
                <div className={classes.root}>
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                </div>
            );
        }

        const disablePhoto =
            client && client.features && client.features.disable_photo === true;

        const requireStudentId =
            client &&
            client.features &&
            client.features.require_id === 'student';

        if (
            ticket &&
            ticket.payment_type === 'dd' &&
            mandates &&
            !mandates.length
        ) {
            return (
                <Redirect
                    to={`/create-mandate/${year}/${client.id}/${serviceId}/${ticketId}`}
                />
            );
        }

        let confirmPurchaseComponent;

        if (ticket && ticket.payment_type === 'dd') {
            confirmPurchaseComponent = (
                <ConfirmDirectDebitPurchase
                    ticket={ticket}
                    passenger={passengerData}
                    client={client}
                    service={service}
                    imagePreviewUrl={imagePreviewUrl}
                    handleEdit={this.handleEditPassenger}
                    disablePhoto={disablePhoto}
                    requireStudentId={requireStudentId}
                />
            );
        } else if (ticket && ticket.payment_type === 'bank') {
            confirmPurchaseComponent = (
                <ConfirmBankPurchase
                    ticket={ticket}
                    passenger={passengerData}
                    client={client}
                    service={service}
                    imagePreviewUrl={imagePreviewUrl}
                    handleEdit={this.handleEditPassenger}
                    disablePhoto={disablePhoto}
                    requireStudentId={requireStudentId}
                    paymentIntent={paymentIntent}
                />
            );
        } else {
            confirmPurchaseComponent = (
                <ConfirmPurchase
                    ticket={ticket}
                    passenger={passengerData}
                    client={client}
                    service={service}
                    imagePreviewUrl={imagePreviewUrl}
                    handleEdit={this.handleEditPassenger}
                    disablePhoto={disablePhoto}
                    requireStudentId={requireStudentId}
                />
            );
        }

        const showAvailabilityWarning =
            'remaining' in ticket &&
            ticket.remaining > 0 &&
            ticket.remaining <= 10;

        return (
            <div className={classes.root}>
                {showAvailabilityWarning && (
                    <div className={classes.availabilityWarning}>
                        <Typography
                            className={classes.availabilityWarningHeading}
                            variant="body2"
                        >
                            {this.formatRemainingTickets(ticket)}
                        </Typography>
                        <Typography
                            className={classes.availabilityWarningText}
                            variant="body1"
                        >
                            If you need more tickets than available please do
                            not purchase this ticket.
                        </Typography>
                    </div>
                )}
                {this.showConfirmPurchase() ? (
                    <div>{confirmPurchaseComponent}</div>
                ) : (
                    <PassengerForm
                        ticket={ticket}
                        passenger={passengerData}
                        serviceAvailability={serviceAvailability}
                        service={service}
                        handleChange={this.handleChange}
                        handleDateChange={this.handleDateChange}
                        handleSubmit={this.handleSubmitPassenger}
                        handleImageChange={this.handleImageChange}
                        imagePreviewUrl={imagePreviewUrl}
                        disablePhoto={disablePhoto}
                        requireStudentId={requireStudentId}
                        imageError={imageError}
                    />
                )}
                {showCheckTickets &&
                    this.renderCheckTicketsModal(ticket, existingTickets)}
            </div>
        );
    }
}

export default withStyles(styles)(CreateTicket);
